import React from "react";

import Error403 from "@/components/UI/Error403";
import ErrorComponent from "@/components/UI/Error";
import { useFetch, is403ApiError } from "@/hooks/FetchHook";
import { isAbsoluteUrl } from "@/utils/apiUrl";
import Loader from "@/components/UI/Loader";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { constructQueryString } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import { buildSiteScopeFilter } from "./dashboardTemplateUtils";
import { withDiscrepancyColumn } from "./discrepancyMarkerColumn";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "@/utils/feature-flag-constants";
import type { TableMetadataType } from "@/types/tableMetadata";
import type {
  Workspace,
  WorkspaceProps,
} from "@/components/UI/TableToolbar/WorkspaceButton/workspace";
import type { TableState } from "@/types/tableState.types";

export type WorkspaceData = {
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (workspaceId: number) => void;
  workspaces: Workspace[];
  initialTableState: TableState;
  mutations: WorkspaceProps["mutations"];
};

type DashboardFetchWrapperProps = {
  datasetName: string;
  subjectId?: string;
  /** Scopes the filter metadata (values and row counts) to a single site. */
  siteId?: string;
  endpointPrefix?: string;
  dataSection?: string;
  children: (data: {
    metadata: TableMetadataType;
    workspace: WorkspaceData;
  }) => React.ReactNode;
};

const DashboardFetchWrapper: React.FC<DashboardFetchWrapperProps> = ({
  datasetName,
  subjectId,
  siteId,
  endpointPrefix,
  dataSection = "all",
  children,
}) => {
  const fullPageName = subjectId ? `${datasetName}-ssj` : datasetName;

  const queryString = constructQueryString({
    baseParams: { dataset_type: datasetName },
    filters: {
      ...(subjectId ? { subject: [subjectId] } : {}),
      ...buildSiteScopeFilter(siteId),
    },
  });

  const metadataEndpointSuffix = isAbsoluteUrl(endpointPrefix)
    ? `filters${queryString}`
    : `filters/${queryString}`;

  const metadataQuery = useFetch({
    endpointPrefix,
    endpointSuffix: metadataEndpointSuffix,
    extraQueryKey: [subjectId, siteId].filter(Boolean).join("-") || undefined,
  });
  const metadata = metadataQuery.data as TableMetadataType | undefined;

  const markersEnabled = useFeatureFlag(FEATURE_FLAGS.DISCREPANCY_MARKERS);
  // Augment once, at the source: the same columns must reach the workspace
  // (which builds column state) and the table (which normalises against them).
  const augmentedMetadata = withDiscrepancyColumn(
    metadata,
    datasetName,
    markersEnabled,
  );

  const workspaceMetadata =
    dataSection !== "all" && augmentedMetadata?.pages?.[dataSection]
      ? augmentedMetadata.pages[dataSection]
      : augmentedMetadata;

  const {
    currentWorkspace,
    setCurrentWorkspace,
    workspaces,
    isLoading: workspaceLoading,
    error: workspaceError,
    initialTableState,
    mutations,
  } = useCurrentWorkspace(fullPageName, workspaceMetadata, dataSection);

  const isLoading = metadataQuery.isLoading || workspaceLoading;
  const error = metadataQuery.error || workspaceError;

  if (error) {
    return (
      <div className="flex mt-[100px] justify-center items-center">
        {is403ApiError(error) ? <Error403 /> : <ErrorComponent />}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {children({
        metadata: augmentedMetadata as TableMetadataType,
        workspace: {
          currentWorkspace,
          setCurrentWorkspace,
          workspaces,
          initialTableState,
          mutations,
        },
      })}
    </>
  );
};

export default DashboardFetchWrapper;
